//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import styled, { useTheme } from 'styled-components';

import Icon from '@components/atoms/icon/Icon';
import { ImageWithErrorHandling } from '@components/atoms/image-with-error-handling/ImageWithErrorHandling';
import { BodyText, SubtitleText } from '@components/atoms/text/Text';
import { Modal } from '@components/molecules/modal/Modal';
import {
  ISharePublisherClient,
  IShareTaggedProductStats,
} from '@share/models/shareStateModel';
import { UiaProductTrackingIds } from '@uia/products/constants/UiaProductConstants';
import { isDefined } from '@utils/filterPredicates';
import { pluralize } from '@utils/stringUtils';

export interface SkuLimitDialogProps {
  isModalOpen: boolean;
  publisher: ISharePublisherClient;
  setIsModalOpen: (isOpen: boolean) => void;
  skuLimit: number;
  taggedProducts: IShareTaggedProductStats[];
}

export const SkuLimitDialog = ({
  isModalOpen,
  publisher,
  setIsModalOpen,
  skuLimit,
  taggedProducts,
}: SkuLimitDialogProps) => {
  const theme = useTheme();

  return (
    <Modal
      closeModal={() => setIsModalOpen(false)}
      contents={
        taggedProducts.length > 0 ? (
          <TextContainer>
            <BodyText>You've tagged the following TCINs:</BodyText>
            <TaggedProductsContainer>
              {taggedProducts.map((product) => (
                <TaggedProduct key={product.productId}>
                  {isDefined(product.productImageUrl) && (
                    <StyledImageWithErrorHandling
                      alt={product.productTitle ?? 'product image'}
                      src={product.productImageUrl}
                    />
                  )}
                  <BodyText ellipsize={2}>{product.productTitle}</BodyText>
                </TaggedProduct>
              ))}
            </TaggedProductsContainer>
            <BodyText>
              If you are over the TCIN limit, {publisher.name} will be in touch
              shortly.
            </BodyText>
          </TextContainer>
        ) : (
          <BodyText>
            Find user-generated content (UGC) using your @mention or #hashtags,
            then tag it with the products you're already selling on{' '}
            {publisher.name}'s website
          </BodyText>
        )
      }
      isModalOpen={isModalOpen}
      primaryButtonInfo={{
        buttonContent: 'Done',
        onClick: () => setIsModalOpen(false),
        trackingId: UiaProductTrackingIds.CLOSE_SKU_LIMIT_MODAL_BUTTON,
      }}
      shouldIncludeSecondaryButton={false}
      title={
        taggedProducts.length > skuLimit ? (
          <SubtitleText>
            <Icon name='alarm' /> You've reached your TCIN limit
          </SubtitleText>
        ) : (
          <SubtitleText>
            {taggedProducts.length > 0
              ? `You've used ${pluralize(taggedProducts.length, 'TCIN', 'TCINs', true)}`
              : "You haven't tagged any products"}
          </SubtitleText>
        )
      }
      width='medium'
      zIndexOverlayOverride={theme.zindex.modal} // to ensure the modal is displayed on top of the UIA overlay
    />
  );
};

const TextContainer = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[3]};
  `,
);

const TaggedProduct = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: start;
    align-item: center;
    gap: ${theme.spacings[3]};
    border: ${theme.borderWidths[1]} solid ${theme.colors.gray1};
    padding-right: ${theme.spacings[3]};
    text-align: start;
  `,
);

const StyledImageWithErrorHandling = styled(ImageWithErrorHandling)(
  ({ theme }) => `
    width: ${theme.spacings[7]};
    aspect-ratio: 1;
  `,
);

const TaggedProductsContainer = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[2]};
    max-height: ${theme.smallMeasure};
    overflow-y: auto;
  `,
);
